import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { IconCheckCircle as CheckCircle2, IconClockFast as Clock, IconFile as FileText, IconLoader as Loader2, IconSearch as Search, IconPlus as Plus, IconTrash as Trash2, IconPencil as PenLine } from "@/components/BrandGraphics";

const STEPS = ["Informations juridiques", "Responsable", "Établissement", "Zone géographique", "Offres"];

// _k : clé React stable (côté navigateur uniquement, retirée avant envoi)
let offerSeq = 0;
const withKey = (o) => ({ ...o, _k: o._k || `offer-${(offerSeq += 1)}` });
const emptyOffer = () => withKey({
  name: "",
  license_type: "Permis B",
  gearbox: "manuelle",
  hours: "",
  price: "",
  accelerated: false,
  description: "",
  availability: "",
});

export default function PartnerOnboarding({ partner, onRefresh }) {
  const status = partner?.status || "DRAFT";
  const [step, setStep] = useState(Math.min(partner?.onboarding_step || 0, 4));
  const [saving, setSaving] = useState(false);
  const [sirenLoading, setSirenLoading] = useState(false);

  const [form, setForm] = useState({
    company_name: partner?.company_name || partner?.name || "",
    trade_name: partner?.trade_name || "",
    siren: partner?.siren || "",
    siret: partner?.siret || "",
    legal_form: partner?.legal_form || "",
    address: partner?.address || "",
    postal_code: partner?.postal_code || "",
    city: partner?.city || "",
    representative_first_name: partner?.representative_first_name || "",
    representative_last_name: partner?.representative_last_name || "",
    representative_role: partner?.representative_role || "",
    phone: partner?.phone || "",
    approval_number: partner?.approval_number || "",
    approval_date: partner?.approval_date || "",
    permis_b: partner?.permis_b ?? true,
    boite_manuelle: partner?.boite_manuelle ?? true,
    boite_automatique: partner?.boite_automatique ?? false,
    formation_acceleree: partner?.formation_acceleree ?? false,
    preparation_examen_rapide: partner?.preparation_examen_rapide ?? false,
    transfert_eleve: partner?.transfert_eleve ?? false,
    coverage: partner?.coverage || "",
    zones: (partner?.zones || []).join(", "),
  });
  const [offers, setOffers] = useState(partner?.offers?.length ? partner.offers.map(withKey) : [emptyOffer()]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const input = "w-full rounded-lg border border-neutral-300 bg-white px-4 py-2.5 outline-none focus:border-brand transition-colors";

  const lookupSiren = async () => {
    if (!form.siren) return;
    setSirenLoading(true);
    try {
      const { data } = await api.get(`/siren/${encodeURIComponent(form.siren)}`);
      if (data.found) {
        setForm((f) => ({
          ...f,
          company_name: data.company_name || f.company_name,
          siret: data.siret || f.siret,
          legal_form: data.legal_form || f.legal_form,
          address: data.address || f.address,
          postal_code: data.postal_code || f.postal_code,
          city: data.city || f.city,
        }));
        toast.success("Informations de l'entreprise récupérées. Vérifiez puis corrigez si besoin.");
      } else {
        toast.error("Entreprise introuvable. Saisissez les informations manuellement.");
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSirenLoading(false);
    }
  };

  const saveStep = async () => {
    setSaving(true);
    try {
      if (step === 0) {
        if (!form.company_name) throw new Error("La raison sociale est obligatoire");
        await api.put("/partner/legal", {
          company_name: form.company_name, trade_name: form.trade_name, siren: form.siren,
          siret: form.siret, legal_form: form.legal_form, address: form.address,
          postal_code: form.postal_code, city: form.city,
        });
      } else if (step === 1) {
        await api.put("/partner/representative", {
          representative_first_name: form.representative_first_name,
          representative_last_name: form.representative_last_name,
          representative_role: form.representative_role, phone: form.phone,
        });
      } else if (step === 2) {
        await api.put("/partner/establishment", {
          approval_number: form.approval_number, approval_date: form.approval_date, address: form.address,
          permis_b: form.permis_b, boite_manuelle: form.boite_manuelle, boite_automatique: form.boite_automatique,
          formation_acceleree: form.formation_acceleree, preparation_examen_rapide: form.preparation_examen_rapide,
          transfert_eleve: form.transfert_eleve,
        });
      } else if (step === 3) {
        await api.put("/partner/zone", {
          coverage: form.coverage,
          zones: form.zones.split(",").map((z) => z.trim()).filter(Boolean),
        });
      } else if (step === 4) {
        const cleaned = offers
          .filter((o) => o.name)
          .map(({ _k, ...o }) => ({ ...o, hours: o.hours ? Number(o.hours) : null, price: o.price ? Number(o.price) : null }));
        await api.put("/partner/offers", { offers: cleaned });
        await api.post("/partner/submit-onboarding");
        toast.success("Dossier envoyé ! Plan Permis va vérifier vos informations.");
        onRefresh();
        return;
      }
      if (step < 4) setStep(step + 1);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- Statuts hors édition ----
  if (status === "PENDING_VERIFICATION") {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="partner-pending-verif">
        <Clock className="mx-auto h-12 w-12 text-amber-500" />
        <h2 className="mt-4 text-2xl font-extrabold text-neutral-900">Dossier en cours de vérification</h2>
        <p className="mt-2 text-neutral-600">
          Merci ! Vos informations ont bien été transmises. L'équipe Plan Permis les vérifie et vous enverra votre
          contrat à signer très prochainement.
        </p>
      </div>
    );
  }
  if (status === "SUSPENDED" || status === "TERMINATED") {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-red-50 p-8 text-center" data-testid="partner-suspended">
        <h2 className="text-2xl font-extrabold text-neutral-900">
          Compte {status === "SUSPENDED" ? "suspendu" : "résilié"}
        </h2>
        <p className="mt-2 text-neutral-600">Contactez Plan Permis pour régulariser votre situation.</p>
      </div>
    );
  }
  if (status === "PENDING_SIGNATURE") {
    return <ContractSign onRefresh={onRefresh} />;
  }

  // ---- Assistant d'inscription ----
  return (
    <div className="mx-auto max-w-2xl" data-testid="partner-onboarding">
      <h1 className="text-2xl font-extrabold tracking-tight text-neutral-900">Finalisez votre inscription partenaire</h1>
      <p className="mt-1 text-sm text-neutral-500">
        Vous recevrez vos premiers prospects après signature de votre contrat.
      </p>

      {/* Stepper */}
      <div className="mt-6 flex flex-wrap gap-2">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
              i === step ? "bg-brand text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-400"
            }`}
          >
            {i < step && <CheckCircle2 className="h-3.5 w-3.5" />}
            {i + 1}. {s}
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6">
        {step === 0 && (
          <div className="space-y-4" data-testid="step-legal">
            <div className="flex gap-2">
              <input className={input} placeholder="SIREN (9 chiffres)" value={form.siren} onChange={(e) => set("siren", e.target.value)} data-testid="siren-input" />
              <button type="button" onClick={lookupSiren} disabled={sirenLoading} data-testid="siren-lookup-btn" className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-brand px-4 text-sm font-semibold text-brand hover:bg-brand/5">
                {sirenLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Vérifier
              </button>
            </div>
            <input className={input} placeholder="Raison sociale *" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="company-name-input" />
            <input className={input} placeholder="Nom commercial" value={form.trade_name} onChange={(e) => set("trade_name", e.target.value)} />
            <div className="grid gap-4 sm:grid-cols-2">
              <input className={input} placeholder="SIRET" value={form.siret} onChange={(e) => set("siret", e.target.value)} />
              <input className={input} placeholder="Forme juridique" value={form.legal_form} onChange={(e) => set("legal_form", e.target.value)} />
            </div>
            <input className={input} placeholder="Adresse" value={form.address} onChange={(e) => set("address", e.target.value)} />
            <div className="grid gap-4 sm:grid-cols-2">
              <input className={input} placeholder="Code postal" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
              <input className={input} placeholder="Ville" value={form.city} onChange={(e) => set("city", e.target.value)} />
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-representative">
            <div className="grid gap-4 sm:grid-cols-2">
              <input className={input} placeholder="Prénom du responsable" value={form.representative_first_name} onChange={(e) => set("representative_first_name", e.target.value)} data-testid="rep-firstname-input" />
              <input className={input} placeholder="Nom du responsable" value={form.representative_last_name} onChange={(e) => set("representative_last_name", e.target.value)} data-testid="rep-lastname-input" />
            </div>
            <input className={input} placeholder="Fonction (ex : gérant)" value={form.representative_role} onChange={(e) => set("representative_role", e.target.value)} />
            <input className={input} placeholder="Téléphone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-establishment">
            <div className="grid gap-4 sm:grid-cols-2">
              <input className={input} placeholder="Numéro d'agrément" value={form.approval_number} onChange={(e) => set("approval_number", e.target.value)} data-testid="approval-number-input" />
              <input className={input} placeholder="Date d'agrément" value={form.approval_date} onChange={(e) => set("approval_date", e.target.value)} />
            </div>
            <input className={input} placeholder="Adresse de l'établissement" value={form.address} onChange={(e) => set("address", e.target.value)} />
            <div className="grid grid-cols-2 gap-3 pt-2">
              {[
                ["permis_b", "Permis B"],
                ["boite_manuelle", "Boîte manuelle"],
                ["boite_automatique", "Boîte automatique"],
                ["formation_acceleree", "Formation accélérée"],
                ["preparation_examen_rapide", "Préparation examen rapide"],
                ["transfert_eleve", "Transfert d'élève"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-neutral-700">
                  <input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="h-4 w-4 accent-brand" />
                  {label}
                </label>
              ))}
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-zone">
            <input className={input} placeholder="Rayon / couverture (ex : 20 km autour de Lyon)" value={form.coverage} onChange={(e) => set("coverage", e.target.value)} />
            <textarea className={input} rows={3} placeholder="Communes / codes postaux couverts (séparés par des virgules)" value={form.zones} onChange={(e) => set("zones", e.target.value)} />
          </div>
        )}
        {step === 4 && (
          <div className="space-y-4" data-testid="step-offers">
            {offers.map((o, i) => (
              <div key={o._k} className="rounded-xl border border-neutral-200 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-neutral-700">Offre {i + 1}</p>
                  {offers.length > 1 && (
                    <button type="button" onClick={() => setOffers(offers.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="mt-3 space-y-3">
                  <input className={input} placeholder="Nom de l'offre (ex : Forfait 20h boîte manuelle)" value={o.name} onChange={(e) => setOffers(offers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`offer-name-${i}`} />
                  <div className="grid gap-3 sm:grid-cols-3">
                    <select className={input} value={o.gearbox} onChange={(e) => setOffers(offers.map((x, j) => (j === i ? { ...x, gearbox: e.target.value } : x)))}>
                      <option value="manuelle">Manuelle</option>
                      <option value="automatique">Automatique</option>
                    </select>
                    <input className={input} type="number" placeholder="Heures" value={o.hours} onChange={(e) => setOffers(offers.map((x, j) => (j === i ? { ...x, hours: e.target.value } : x)))} />
                    <input className={input} type="number" placeholder="Prix (€)" value={o.price} onChange={(e) => setOffers(offers.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} data-testid={`offer-price-${i}`} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-neutral-700">
                    <input type="checkbox" checked={o.accelerated} onChange={(e) => setOffers(offers.map((x, j) => (j === i ? { ...x, accelerated: e.target.checked } : x)))} className="h-4 w-4 accent-brand" />
                    Formation accélérée
                  </label>
                </div>
              </div>
            ))}
            <button type="button" onClick={() => setOffers([...offers, emptyOffer()])} data-testid="add-offer-btn" className="inline-flex items-center gap-2 rounded-lg border border-dashed border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-600 hover:border-brand hover:text-brand">
              <Plus className="h-4 w-4" /> Ajouter une offre
            </button>
          </div>
        )}

        <div className="mt-6 flex justify-between">
          <button type="button" disabled={step === 0 || saving} onClick={() => setStep(step - 1)} className="rounded-full border border-neutral-300 px-6 py-2.5 text-sm font-semibold text-neutral-600 disabled:opacity-40">
            Précédent
          </button>
          <button type="button" onClick={saveStep} disabled={saving} data-testid="onboarding-next-btn" className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {step === 4 ? "Envoyer mon dossier" : "Suivant"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContractSign({ onRefresh }) {
  const [data, setData] = useState(null);
  const [accept, setAccept] = useState(false);
  const [fullName, setFullName] = useState("");
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    api.get("/partner/contract").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const sign = async () => {
    if (!accept || !fullName.trim()) {
      toast.error("Cochez l'acceptation et saisissez votre nom complet.");
      return;
    }
    setSigning(true);
    try {
      await api.post("/partner/contract/sign", { accept, full_name: fullName.trim() });
      toast.success("Contrat signé ! Votre compte est activé.");
      onRefresh();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSigning(false);
    }
  };

  if (!data) return <p className="text-neutral-400">Chargement du contrat…</p>;
  const c = data.contract;
  if (!c) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
        <Clock className="mx-auto h-12 w-12 text-amber-500" />
        <p className="mt-4 text-neutral-600">Votre contrat est en cours de préparation par Plan Permis.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl" data-testid="contract-sign">
      <div className="flex items-center gap-2 text-brand">
        <FileText className="h-6 w-6" />
        <h1 className="text-2xl font-extrabold tracking-tight text-neutral-900">Votre contrat de partenariat</h1>
      </div>
      <p className="mt-1 text-sm text-neutral-500">Contrat {c.contract_number} · version {c.contract_version}</p>

      <div
        className="prose prose-sm mt-5 max-h-[420px] overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-6"
        data-testid="contract-body"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.html || "") }}
      />

      <a href={`${api.defaults.baseURL}/partner/contract/pdf`} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-semibold text-brand hover:underline">
        Télécharger le PDF
      </a>

      <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-6">
        <label className="flex items-start gap-3 text-sm text-neutral-700">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1 h-4 w-4 accent-brand" data-testid="contract-accept-checkbox" />
          <span>J'ai lu et j'accepte l'intégralité des conditions du contrat de partenariat Plan Permis.</span>
        </label>
        <input
          className="mt-4 w-full rounded-lg border border-neutral-300 px-4 py-2.5 outline-none focus:border-brand"
          placeholder="Tapez votre nom et prénom pour signer"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          data-testid="contract-signer-input"
        />
        <button onClick={sign} disabled={signing} data-testid="contract-sign-btn" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-3.5 text-base font-bold text-white hover:bg-brand-hover disabled:opacity-60">
          {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
          Signer électroniquement et activer mon compte
        </button>
      </div>
    </div>
  );
}
